import styles from './PunchupSelect.module.css';

import { ChevronDown } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { FormSelectProps } from 'features/common/components/Form/types';
import { useFormContext } from 'react-hook-form';
import { PunchupFieldLabel } from '../../fields/PunchupFieldLabel';

const CheckMark = ({ styles }: { styles: Record<string, string> }) => (
	<span className={styles.optionCheckBox} aria-hidden="true">
		<svg className={styles.optionCheckSvg} viewBox="0 0 10 8" fill="none" aria-hidden="true">
			<path
				d="M1 4L3.5 6.5L9 1"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

interface PunchupFormSelectProps extends FormSelectProps {
	help?: string;
	/**
	 * Read-only override of WHICH option reads as selected — ignored unless `disabled`.
	 *
	 * A disabled select is a readout, and a readout's truth can live outside the form value: the locked
	 * pool field shows the pool resolved from the pools feed, because the ticket-type GET does not
	 * reliably carry the FK. Passing it here keeps the display honest without writing the form (which
	 * would either dirty the form or silently change what Save sends).
	 *
	 * `null` means "nothing to select" — no option matches, so the placeholder renders. Use it when the
	 * value genuinely cannot be resolved; never substitute a guessed option.
	 */
	displayValue?: string | number | null;
}

/**
 * PunchupFormSelect
 *
 * Custom styled dropdown replacing the native <select>.
 * Visual reference: downstroke shadcn Select (WaitlistClient).
 *
 * Uses setValue/watch from react-hook-form context so it integrates
 * identically to PunchupInput — no Controller wrapper needed at the callsite.
 */
const PunchupFormSelect: FC<PunchupFormSelectProps> = ({
	id,
	name,
	options,
	label,
	withDefaultValue,
	placeholder,
	help,
	disabled,
	displayValue,
}) => {
	const {
		watch,
		setValue,
		formState: { errors, dirtyFields },
	} = useFormContext();

	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const currentValue = watch(name);
	const error = errors[name];
	// A disabled field cannot carry a meaningful unsaved change (docs/forms.md → dirty suppression).
	const isFieldDirty = !disabled && !!dirtyFields[name as string];

	const shownValue = disabled && displayValue !== undefined ? displayValue : currentValue;
	const selectedOption = options.find((o) => String(o.value) === String(shownValue));
	const fallbackLabel = placeholder ?? (withDefaultValue ? 'Select One...' : '');
	const displayLabel = selectedOption?.label ?? fallbackLabel;
	const isPlaceholder = !selectedOption;

	/* Close on outside click */
	useEffect(() => {
		const handleOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, []);

	/* Close on Escape */
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsOpen(false);
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, []);

	const handleSelect = (value: string | number) => {
		setValue(name, value, { shouldValidate: true, shouldDirty: true });
		setIsOpen(false);
	};

	return (
		<div ref={wrapperRef} style={{ width: '100%' }}>
			{label && <PunchupFieldLabel label={label} htmlFor={id ?? name} help={help} />}

			<div className={styles.wrapper}>
				<button
					type="button"
					id={id ?? name}
					disabled={disabled}
					aria-haspopup="listbox"
					aria-expanded={isOpen}
					className={[
						styles.trigger,
						isOpen ? styles.triggerOpen : null,
						isFieldDirty && !error && !isOpen ? styles.triggerDirty : null,
					]
						.filter(Boolean)
						.join(' ')}
					onClick={() => setIsOpen((prev) => !prev)}
				>
					<span className={isPlaceholder ? styles.placeholder : undefined}>{displayLabel}</span>
					<ChevronDown size={16} className={`${styles.chevron}${isOpen ? ` ${styles.chevronOpen}` : ''}`} />
				</button>

				{/* `!disabled` is not redundant: a select can become disabled while its panel is open (a refetch
				    that reports the first sale locks the pool field), and an open panel would still commit. */}
				{isOpen && !disabled && (
					<div role="listbox" className={styles.panel}>
						{withDefaultValue && (
							<button
								type="button"
								role="option"
								aria-selected={!currentValue}
								className={`${styles.option}${!currentValue ? ` ${styles.optionSelected}` : ''}`}
								onClick={() => handleSelect('')}
							>
								Select One...
								{!currentValue && <CheckMark styles={styles} />}
							</button>
						)}
						{options.map((option) => {
							const isSelected = String(option.value) === String(currentValue);
							return (
								<button
									key={option.value}
									type="button"
									role="option"
									aria-selected={isSelected}
									className={`${styles.option}${isSelected ? ` ${styles.optionSelected}` : ''}`}
									onClick={() => handleSelect(option.value)}
								>
									{option.label}
									{isSelected && <CheckMark styles={styles} />}
								</button>
							);
						})}
					</div>
				)}
			</div>

			{error?.message && <span className="punchupFormError">{error.message as string}</span>}
		</div>
	);
};

export { PunchupFormSelect };
